import { Typography, Button, Box } from '@material-ui/core'
import React, { useState } from 'react'
import { actionCreators as transportActionCreators } from '../store/TransportApp';
import { actionCreators as modemsActionCreators } from '../store/Modems';
import { Link as RouterLink } from 'react-router-dom'
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables'
import { useDispatch, useSelector } from 'react-redux'
import { ApplicationState } from '../store'
import ConnectionButton from './ModemConnectionButton'
import { AllGlobal, useIsUserInRoles } from '../hooks/useIsUserInRoles';
import { ModemWithOrgSiteResponseDTO, SiteResponseDTO, SitesClient, TransportEclypseIpSetting, TransportModemIpSetting, HeartbeatDTO, ModemsClient, DeviceTwinOrHeartbeatRequestedType, ModemHeartbeatResponseDTO, HeartbeatResponse, ReportedTwinDTO } from '../generated/ApiClient'
import { DateTime, Zone } from 'luxon'
import { Dispatch } from 'redux';
import { insertIf, tableOptions as options, utcToLocal } from '../common/Shared'

const tableOptions = {
    ...options,
    selectableRowsHideCheckboxes: true
}

interface ModemWithSiteTwinTableData {
    modemHostId: string,
    label: string,
    iccid: string | undefined,
    imei: string | undefined,
    siteName: string | undefined,
    siteAddress: string | undefined,
    lastTimeOnline: DateTime | undefined,
    remoteConnectivityEnabled: string,
    orderNumber: string | undefined,
    detailsButton: string
}

export function ToTableData(m: ModemWithOrgSiteResponseDTO, siteData: SiteResponseDTO[], lastonlinetime?: HeartbeatResponse | undefined, twin?: ReportedTwinDTO | undefined): ModemWithSiteTwinTableData {
    let siteName = 'No Site Assigned';
    let siteAddress = '';
    if (m.siteId) {
        const site = siteData.find(s => s.externalId === m.siteId);
        if (site) {
            siteName = site.name;
            siteAddress = (site.address ?? "") + (site.city ? `, ${site.city}` : "") + (site.stateProv ? `, ${site.stateProv}` : "");
        }
    }
    return {
        modemHostId: m.modemHostId,
        label: m.label,
        iccid: twin?.iccid,
        imei: twin?.imei,
        siteName,
        siteAddress,
        lastTimeOnline: lastonlinetime ? DateTime.fromISO((lastonlinetime.Fields as HeartbeatDTO).lastOnlineTime, { zone: 'UTC' }) : undefined,
        remoteConnectivityEnabled: m.remoteConnectivityEnabled === true ? 'True' : 'False',
        orderNumber: m.orderNumber,
        detailsButton: m.modemHostId
    }
}

function disableButton(modem: ModemWithOrgSiteResponseDTO) {
    const isGlobalUser = useIsUserInRoles(AllGlobal);
    return !isGlobalUser && !modem.remoteConnectivityEnabled
}

export function modemTableColumns(modems: ModemWithOrgSiteResponseDTO[], includeTwinColumns?: boolean, heartbeats?: { [k: string]: HeartbeatResponse | undefined; } | undefined, transportEntries?: [string, TransportModemIpSetting | TransportEclypseIpSetting][] | undefined, dispatch?: Dispatch<any> | undefined): MUIDataTableColumnDef[] {
    return [
        {
            name: "label",
            label: "Label",
            options: {
                filter: false,
                sort: true,
            }
        },
        {
            name: "modemHostId",
            label: "Modem Host Id",
            options: {
                filter: false,
                sort: true,
            }
        },
        ...insertIf(includeTwinColumns, {
            name: "iccid",
            label: "ICCID",
            options: {
                filter: false,
                sort: true,
            }
        }),
        ...insertIf(includeTwinColumns, {
            name: "imei",
            label: "IMEI",
            options: {
                filter: false,
                sort: true,
            }
        }),
        {
            name: "siteName",
            label: "Site",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: "siteAddress",
            label: "Address",
            options: {
                filter: true,
                sort: true,
            }
        },
        ...insertIf(heartbeats !== undefined, {
            name: "lastTimeOnline",
            label: "Last Time Online",
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    return <Typography>{utcToLocal((heartbeats![modems[dataIndex].modemHostId]?.Fields as HeartbeatDTO)?.lastOnlineTime).toLocaleString(DateTime.DATETIME_MED)}</Typography>
                }
            }
        }),
        {
            name: "remoteConnectivityEnabled",
            label: "Remote Connectivity Enabled",
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: "detailsButton",
            label: "Details",
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    return <Button component={RouterLink} variant='contained' size='small' to={`/modems/${modems[dataIndex].modemHostId}`}>View</Button>
                }
            }
        }        
    ]
}

export function RenderFullModemsTable(modems: ModemWithOrgSiteResponseDTO[], siteData: SiteResponseDTO[], heartbeats: {
    [k: string]: HeartbeatResponse | undefined;
}, twins: {
    [key: string]: ReportedTwinDTO | undefined;
}, transportEntries: [string, TransportModemIpSetting | TransportEclypseIpSetting][], dispatch: Dispatch<any>) {
    return <MUIDataTable title={""} data={modems.map(m => ToTableData(m, siteData, heartbeats[m.modemHostId], twins[m.modemHostId]))} columns={modemTableColumns(modems, true, heartbeats, transportEntries, dispatch)} options={tableOptions} />
}

export function Modems(props: { organizationId?: string }) {
    const dispatch = useDispatch();

    const [siteData, setSiteData] = useState<SiteResponseDTO[]>([]);
    const [mdmHeartbeat, setmdmHeartbeat] = useState<ModemHeartbeatResponseDTO[]>([]);

    const transportConfigurationSettings = useSelector<ApplicationState,
        { [ip: string]: TransportModemIpSetting | TransportEclypseIpSetting } | undefined>(state => {
            return state.transportAppState?.transportConfig?.simulatorToRemoteIpMap
        });
    const allModems = useSelector<ApplicationState,
        { [key: string]: ModemWithOrgSiteResponseDTO } | undefined>(state => {
            return state.modems?.modems
        })
    const twins = useSelector<ApplicationState,
        {
            [key: string]: {
                simpleTwin?: ReportedTwinDTO | undefined;
                completeTwin?: unknown;
            } | undefined
        } | undefined>(state => {
            return state.modems?.deviceTwins
        })
    React.useEffect(() => {
        dispatch(modemsActionCreators.requestModems())
        const fetchData = async () => {
            const siteData = await new SitesClient().getMany2();
            setSiteData(siteData);
        }
        fetchData();
    }, [props.organizationId, setSiteData]);

    React.useEffect(() => {
        const fetchData = async () => {
            if (allModems && Object.keys(allModems).length > 0) {
                const heartbeats = await new ModemsClient().getHeartbeats(DeviceTwinOrHeartbeatRequestedType.Simple, { modemIds: Object.keys(allModems ?? {}) })
                setmdmHeartbeat(heartbeats);
            }
        }
        fetchData();
    }, [allModems, setmdmHeartbeat]);

    React.useEffect(() => {
        if (allModems && Object.keys(allModems).length > 0) {
            dispatch(modemsActionCreators.requestManyTwins(Object.keys(allModems ?? {}), DeviceTwinOrHeartbeatRequestedType.Simple))
        }
    }, [allModems, setmdmHeartbeat]);

    const orgId = props.organizationId;
    const transportEntries = Object.entries(transportConfigurationSettings ?? {})
    let modemsData = Object.values(allModems ?? {}).map(m => m as ModemWithOrgSiteResponseDTO)
    let heartbeats = Object.fromEntries(mdmHeartbeat.map(m => [m.modemHostId, m.heartbeat]))
    if (orgId) {
        modemsData = modemsData.filter(m => m.organizationId === orgId);
    }
    return RenderFullModemsTable(modemsData, siteData, heartbeats, Object.fromEntries(Object.entries(twins ?? {}).map(([s, twin]) => [s, twin?.simpleTwin])), transportEntries, dispatch)
}

export function ModemsPage() {
    return (
        <>
            <Typography variant='h4' id="tabelLabel">Modems</Typography>
            <Box mt={1}>
                <Modems organizationId={undefined} />
            </Box>
        </>
    )
}
